import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { DeviceData, DeviceColumn } from './aditools-device-table.ts';

const TRANSLATIONS = {
  en: {
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    default: 'Default',
  },
  cn: {
    selectAll: '全部选择',
    deselectAll: '全部取消',
    default: '默认',
  },
  jp: {
    selectAll: 'すべて選択',
    deselectAll: 'すべての選択を解除',
    default: 'デフォルト',
  },
  ru: {
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    default: 'Default',
  },
  /*
  ru: {
    selectAll: 'Выбрать все',
    deselectAll: 'Отменить выбор',
    default: 'По умолчанию',
  },
  */
};

interface AditoolsParameterSelectorSignature {
  Args: AditoolsParameterSelectorArgs;
  Element: HTMLDivElement;
}
interface AditoolsParameterSelectorArgs {
  locale: string;
  columns: DeviceColumn<DeviceData>[];
  selectedColumns: DeviceColumn<DeviceData>[];
  selectionChanged: (newColumns: DeviceColumn<DeviceData>[]) => void;
}

interface SelectorColumn {
  classification: string;
  state: { index: number; selected: boolean }[];
}

export default class AditoolsParameterSelector extends Component<AditoolsParameterSelectorSignature> {
  get selectorColumns(): SelectorColumn[] {
    const columns: SelectorColumn[] = [];
    const classifications = this.getClassifications(this.args.columns);
    classifications.forEach((classification) => {
      const state: { index: number; selected: boolean }[] = [];
      this.args.columns.forEach((column, index) => {
        if (column.classification === classification && column.id !== '0') {
          let selected = !!column.isDefault;
          if (this.args.selectedColumns.length > 0) {
            selected = false;
            this.args.selectedColumns.forEach((item) => {
              if (column.name === item.name && column.minMaxTyp === item.minMaxTyp) {
                selected = true;
              }
            });
          }
          state.push({ index: index, selected: selected });
        }
      });
      if (state.length > 0) {
        columns.push({ classification: classification ?? '', state: state });
      }
    });
    return columns;
  }

  getClassifications(columns: DeviceColumn<DeviceData>[]): string[] {
    const classifications: string[] = [];
    columns.forEach((column) => {
      if (column.classification) {
        if (classifications.length === 0) {
          classifications.push(column.classification);
        }
        if (!classifications.includes(column.classification)) {
          classifications.push(column.classification);
        }
      }
    });
    return classifications.sort();
  }

  get _translations(): Record<string, string> {
    let locale: 'en' | 'cn' | 'jp' | 'ru' = 'en';
    if (
      this.args.locale === 'en' ||
      this.args.locale === 'cn' ||
      this.args.locale === 'jp' ||
      this.args.locale === 'ru'
    ) {
      locale = this.args.locale;
    }
    return TRANSLATIONS[locale as 'en' | 'cn' | 'jp' | 'ru'];
  }

  @action selectAllParameters(): void {
    const checkboxes = document.querySelectorAll('.parameter-selector-checkbox>input[type=checkbox]');
    checkboxes.forEach((checkbox) => {
      (checkbox as HTMLInputElement).checked = true;
    });

    if (this.args.selectionChanged) {
      this.args.selectionChanged(this.args.columns);
    }
  }

  @action deselectAllParameters(): void {
    const checkboxes = document.querySelectorAll('.parameter-selector-checkbox>input[type=checkbox]');
    checkboxes.forEach((checkbox) => {
      const splitId = checkbox.id.split('_');
      const index = parseInt(splitId[1] ?? '-1'); // The ?? clause will never happen because the id carries the column index
      // ensures checkbox element for "Part Name" is checked
      if (index != 0) {
        (checkbox as HTMLInputElement).checked = false;
      }
    });
    if (this.args.selectionChanged) {
      this.args.selectionChanged(
        this.args.columns.filter((x) => {
          return x.id == '0';
        }),
      );
    }
  }

  @action defaultParameters(): void {
    const checkboxes = document.querySelectorAll('.parameter-selector-checkbox>input[type=checkbox]');
    checkboxes.forEach((checkbox) => {
      const splitId = checkbox.id.split('_');
      const index = parseInt(splitId[1] ?? '-1'); // The ?? clause will never happen because the id carries the column index
      const column = this.args.columns[index];
      (checkbox as HTMLInputElement).checked = !!column && !!column.isDefault;
    });

    if (this.args.selectionChanged) {
      this.args.selectionChanged(
        this.args.columns.filter((x) => {
          return x.isDefault;
        }),
      );
    }
  }

  @action selectionChanged(): void {
    if (this.args.selectionChanged) {
      const newColumns: DeviceColumn<DeviceData>[] = this.args.columns.filter((x) => {
        return x.id == '0'; // Columns should always include the part number
      });
      const checkboxes = document.querySelectorAll('.parameter-selector-checkbox>input[type=checkbox]');
      const columnIndices: number[] = [];
      checkboxes.forEach((checkbox) => {
        if ((checkbox as HTMLInputElement).checked) {
          const index = parseInt(checkbox.id.split('_')[1] ?? '-1');
          columnIndices.push(index);
        }
      });

      if (columnIndices.length > 0) {
        columnIndices
          .sort((n1: number, n2: number) => n1 - n2)
          .forEach((index) => {
            const column = this.args.columns[index];
            if (column) {
              newColumns.push(column);
            }
          });
      }
      this.args.selectionChanged(newColumns);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    AditoolsParameterSelector: typeof AditoolsParameterSelector;
  }
}
